import React, { createContext, useContext, useEffect, useMemo, useState, } from "react";
import Attribution from "ol/control/Attribution";
import OlMap from "ol/Map";
import Projection from "ol/proj/Projection";
import { get as getProj } from "ol/proj/projections";
import View from "ol/View";
import loadProjection from "../utils/loadProjection";
export const MapContext = createContext({});
/**
 * Utility to get the `MapContext`.
 */
export function useMapContext() {
    return useContext(MapContext);
}
/**
 * Utility to get the `ol/Map` instance.
 */
export function useMap() {
    return useContext(MapContext).map;
}
/**
 * Creates the `ol/Map` and context. Does not create a map target (and will
 * alone not render an actual map), use `<MapView>` to place the map in your
 * app.
 *
 * At most one `<MapView>` should be rendered in the component tree below the
 * `<MapProvider>`, otherwise the last one rendered will "steal" the map
 * canvas.
 *
 * If the provided projection is a string (e.g. `"EPSG:32633"`), and the
 * projection definition is not already loaded, the definition is loaded from
 * epsg.io automatically. Use `proj4.def()` and `ol/proj/proj4/register()` to
 * avoid this.
 *
 * Note: `center` and `zoom`, or `extent`, must be set to render any content.
 */
export default function MapProvider({ projection = "EPSG:3857", noInteractions = false, children, keyboardEventTarget, auth, ...viewOptions }) {
    return (React.createElement(EnsureProjection, { projection: projection },
        React.createElement(MapProviderInternal, { projection: projection, noInteractions: noInteractions, keyboardEventTarget: keyboardEventTarget, auth: auth, ...viewOptions }, children)));
}
function EnsureProjection({ projection, children, }) {
    const [loadedTrigger, setLoadedTrigger] = useState(false);
    const isProjectionLoaded = useMemo(() => {
        loadedTrigger; // Dummy reference
        if (projection instanceof Projection)
            return true;
        const proj = getProj(projection);
        if (proj === null) {
            return false;
        }
        return true;
    }, [projection, loadedTrigger]);
    useEffect(() => {
        if (isProjectionLoaded || projection instanceof Projection)
            return;
        const epsg = projection;
        loadProjection(epsg).then(() => setLoadedTrigger(true));
    }, [isProjectionLoaded, projection]);
    if (!isProjectionLoaded)
        return (React.createElement("div", { style: { padding: 20, textAlign: "center" } }, "Loading projection..."));
    return React.createElement(React.Fragment, null, children);
}
function MapProviderInternal({ projection, noInteractions = false, children, keyboardEventTarget, initialExtent, auth, ...viewOptions }) {
    const [context, setContext] = useState(() => createMapContext({ ...viewOptions, projection }, initialExtent, noInteractions, keyboardEventTarget, auth !== null && auth !== void 0 ? auth : {}, "init"));
    useEffect(() => {
        const code = projection instanceof Projection ? projection.getCode() : projection;
        if (context.view.getProjection().getCode() !== code) {
            setContext((context) => createMapContext({ ...context.viewOptions, projection }, undefined, noInteractions, context.keyboardEventTarget, context.auth, "projchange"));
        }
    }, [projection, noInteractions, context.view]);
    return React.createElement(MapContext.Provider, { value: context }, children);
}
function createMapContext(viewOptions, initialExtent, noInteractions, keyboardEventTarget, auth, reason) {
    const map = new OlMap({
        controls: [new Attribution({ collapsible: false })],
        interactions: noInteractions ? [] : undefined,
        layers: [],
        overlays: [],
        keyboardEventTarget: keyboardEventTarget,
        maxTilesLoading: 16,
        moveTolerance: 3,
        pixelRatio: 1,
        target: undefined,
        view: new View({
            ...viewOptions,
        }),
    });
    map.on("error", function (e) {
        console.warn("map error", e);
    });
    console.debug("Create map", reason);
    const view = map.getView();
    const getPaddedCenter = () => {
        var _a;
        // TODO
        return (_a = view.getCenter()) !== null && _a !== void 0 ? _a : [0, 0];
    };
    const forceUpdateMap = () => {
        map.updateSize();
        map.render();
    };
    const fitViewAndZoom = (extentOrGeometry, maxZoom, duration = 400) => {
        view.fit(extentOrGeometry, { maxZoom, duration });
    };
    const setCenterAndZoom = (center, zoom, duration = 400) => {
        if (view.getAnimating()) {
            view.cancelAnimations();
        }
        view.animate({ center, zoom, duration });
    };
    const setZoom = (zoom, duration = 400) => {
        setCenterAndZoom(getPaddedCenter(), zoom, duration);
    };
    const zoomIn = (duration = 400) => {
        var _a;
        const zoom = (_a = view.getZoom()) !== null && _a !== void 0 ? _a : 0;
        if (zoom > view.getMaxZoom() - 1)
            return;
        setCenterAndZoom(getPaddedCenter(), zoom + 1, duration);
    };
    const zoomOut = (duration = 400) => {
        var _a;
        const zoom = (_a = view.getZoom()) !== null && _a !== void 0 ? _a : 0;
        if (zoom < view.getMinZoom() + 1)
            return;
        setCenterAndZoom(getPaddedCenter(), zoom - 1, duration);
    };
    return {
        map,
        view,
        auth,
        viewOptions,
        keyboardEventTarget,
        initialExtent,
        forceUpdateMap,
        fitViewAndZoom,
        setCenterAndZoom,
        setZoom,
        zoomIn,
        zoomOut,
    };
}
